import React, { Fragment, useState, useEffect } from 'react';
import { Card, CardBody, CardFooter, Col, Container, FormGroup, Input, Label, Row } from 'reactstrap';
import axios from 'axios';
import { Btn, H6 } from '../../../../AbstractElements';
import config from '../../../../config';
import { toast } from 'react-toastify';

// A Directory (tenant) ID is a GUID
const GUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// The well known Microsoft account (consumer) tenant
const MSA_TENANT_ID = '9188040d-6c67-4c5b-b112-36a304b66dad';

const DOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

const FREE_MAILBOX_DOMAINS = [
    'gmail.com', 'googlemail.com', 'outlook.com', 'outlook.in', 'hotmail.com', 'hotmail.co.uk',
    'live.com', 'live.in', 'msn.com', 'yahoo.com', 'yahoo.co.in', 'yahoo.co.uk', 'ymail.com',
    'rocketmail.com', 'rediffmail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
    'protonmail.com', 'proton.me', 'zoho.com', 'zohomail.com', 'mail.com', 'gmx.com',
    'gmx.net', 'yandex.com', 'yandex.ru', 'inbox.com', 'fastmail.com', 'tutanota.com'
];

const SSOConfigurationAdd = () => {

    // Authorization is already enforced by the Configurations page that hosts this tab

    const concessionaire_id = localStorage.getItem('concessionaireId');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const configheader = {
        headers: {
            Authorization: `${token}`
        }
    };

    const [ssoEnabled, setSsoEnabled] = useState(false);
    const [directories, setDirectories] = useState([]);
    const [directoryInput, setDirectoryInput] = useState('');
    const [domains, setDomains] = useState([]);
    const [domainInput, setDomainInput] = useState('');
    const [provisioningMode, setProvisioningMode] = useState('existing_only');
    const [defaultUserTypeId, setDefaultUserTypeId] = useState('');
    const [connectedCount, setConnectedCount] = useState(0);
    const [userProfiles, setUserProfiles] = useState([]);
    const [properties, setProperties] = useState([]);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios
            .post(`${config.hostname}/concessionairesso/getssoconfig`, { concessionaire_id }, configheader)
            .then((response) => {
                setSsoEnabled(response.data.sso_enabled || false);
                setDirectories(response.data.entra_directories || []);
                setDomains(response.data.verified_domains || []);
                setProvisioningMode(response.data.provisioning_mode || 'existing_only');
                setConnectedCount(response.data.connected_user_count || 0);
                const startingRole = response.data.default_user_type_id;
                setDefaultUserTypeId(startingRole ? (startingRole._id || startingRole) : '');
                setSelectedProperties((response.data.default_property_ids || []).map(String));
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        axios
            .post(`${config.hostname}/userpermissions/getpermissionbyconcessionaire`, { concessionaire_id }, configheader)
            .then((response) => {
                setUserProfiles(response.data);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        axios
            .post(`${config.hostname}/property/getpropertybyconcessionaire`, { property_concessionaire_id: concessionaire_id }, configheader)
            .then((response) => {
                setProperties(response.data || []);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    const toggleProperty = (propertyId) => {
        setSelectedProperties(prev => prev.includes(propertyId)
            ? prev.filter(id => id !== propertyId)
            : [...prev, propertyId]);
    };

    const handleAddDirectory = () => {
        const directory = directoryInput.trim().toLowerCase();
        if (!directory) return;
        if (!GUID_PATTERN.test(directory)) {
            toast.error('Enter a valid Directory (tenant) ID');
            return;
        }
        if (directory === MSA_TENANT_ID) {
            toast.error('Personal Microsoft accounts cannot be linked. Use a work directory.');
            return;
        }
        if (directories.includes(directory)) {
            toast.error('This directory is already added');
            return;
        }
        setDirectories([...directories, directory]);
        setDirectoryInput('');
    };

    const handleRemoveDirectory = (directory) => {
        setDirectories(directories.filter(item => item !== directory));
    };

    const handleAddDomain = () => {
        const domain = domainInput.trim().toLowerCase().replace(/^@/, '');
        if (!domain) return;
        if (!DOMAIN_PATTERN.test(domain)) {
            toast.error('Enter a valid email domain');
            return;
        }
        if (FREE_MAILBOX_DOMAINS.includes(domain)) {
            toast.error(`${domain} is a free mailbox provider and cannot be verified`);
            return;
        }
        if (domains.includes(domain)) {
            toast.error('This domain is already added');
            return;
        }
        setDomains([...domains, domain]);
        setDomainInput('');
    };

    const handleRemoveDomain = (domain) => {
        setDomains(domains.filter(item => item !== domain));
    };

    const handleSave = () => {
        if (ssoEnabled && directories.length === 0) {
            toast.error('Add at least one Microsoft directory before allowing Microsoft sign-in');
            return;
        }
        if (ssoEnabled && domains.length === 0) {
            toast.error('Add at least one verified email domain before allowing Microsoft sign-in');
            return;
        }
        if (provisioningMode === 'auto_create' && !defaultUserTypeId) {
            toast.error('Select a starting role for people created on first sign-in');
            return;
        }

        const data = {
            concessionaire_id,
            sso_enabled: ssoEnabled,
            entra_directories: directories,
            verified_domains: domains,
            provisioning_mode: provisioningMode,
            default_user_type_id: provisioningMode === 'auto_create' ? defaultUserTypeId : null,
            default_property_ids: provisioningMode === 'auto_create' ? selectedProperties : [],
            login_id
        };

        setSaving(true);
        axios
            .post(`${config.hostname}/concessionairesso/savessoconfig`, data, configheader)
            .then((response) => {
                setConnectedCount(response.data.connected_user_count || 0);
                toast.success('Successfully Saved');
                setSaving(false);
            })
            .catch((error) => {
                console.log(error);
                toast.error(error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Error');
                setSaving(false);
            });
    };

    const chipStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: '#f4f4f4',
        border: '1px solid #e6e6e6',
        borderRadius: '4px',
        padding: '4px 10px',
        marginRight: '8px',
        marginBottom: '8px',
        fontSize: '13px'
    };

    const chipRemoveStyle = {
        cursor: 'pointer',
        marginLeft: '8px',
        color: '#898989',
        fontWeight: 600,
        lineHeight: 1
    };

    return (
        <Fragment>
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>

                                <Row className="align-items-center mb-4">
                                    <Col sm="10">
                                        <H6>Allow Microsoft sign-in</H6>
                                        <span className="f-12 f-light">
                                            Staff in a linked Microsoft directory can sign in with their work account.
                                            {connectedCount > 0
                                                ? ` ${connectedCount} ${connectedCount === 1 ? 'person has' : 'people have'} already connected.`
                                                : ''}
                                        </span>
                                    </Col>
                                    <Col sm="2" className="text-end">
                                        <div className="form-check form-switch d-inline-block">
                                            <Input
                                                type="switch"
                                                role="switch"
                                                id="ssoEnabled"
                                                checked={ssoEnabled}
                                                onChange={(e) => setSsoEnabled(e.target.checked)}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <FormGroup className="mb-4">
                                    <Label className="form-label">Microsoft directories</Label>
                                    <div className="f-12 f-light mb-2">
                                        The customer IT admin finds this in Microsoft Entra under Overview &rarr; Directory (tenant) ID.
                                        This is what decides who reaches this concessionaire, so only add directories you have verified.
                                    </div>
                                    <div>
                                        {directories.map(directory => (
                                            <span key={directory} style={chipStyle}>
                                                {directory}
                                                <span style={chipRemoveStyle} onClick={() => handleRemoveDirectory(directory)}>&times;</span>
                                            </span>
                                        ))}
                                    </div>
                                    <Row className="align-items-center">
                                        <Col md="9">
                                            <Input
                                                className="form-control"
                                                type="text"
                                                placeholder="00000000-0000-0000-0000-000000000000"
                                                value={directoryInput}
                                                onChange={(e) => setDirectoryInput(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDirectory(); } }}
                                            />
                                        </Col>
                                        <Col md="3">
                                            <Btn attrBtn={{ color: 'primary', outline: true, type: 'button', onClick: handleAddDirectory }}>Add</Btn>
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup className="mb-4">
                                    <Label className="form-label">Verified email domains</Label>
                                    <div className="f-12 f-light mb-2">
                                        Used to decide whether a Microsoft account can connect itself to an existing user
                                        automatically. Free mailbox providers such as gmail.com are refused.
                                    </div>
                                    <div>
                                        {domains.map(domain => (
                                            <span key={domain} style={chipStyle}>
                                                {domain}
                                                <span style={chipRemoveStyle} onClick={() => handleRemoveDomain(domain)}>&times;</span>
                                            </span>
                                        ))}
                                    </div>
                                    <Row className="align-items-center">
                                        <Col md="9">
                                            <Input
                                                className="form-control"
                                                type="text"
                                                placeholder="acme.com"
                                                value={domainInput}
                                                onChange={(e) => setDomainInput(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain(); } }}
                                            />
                                        </Col>
                                        <Col md="3">
                                            <Btn attrBtn={{ color: 'primary', outline: true, type: 'button', onClick: handleAddDomain }}>Add</Btn>
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup className="mb-2">
                                    <Label className="form-label">When someone signs in with Microsoft for the first time</Label>

                                    <div className="form-check mb-3">
                                        <Input
                                            type="radio"
                                            name="provisioningMode"
                                            id="existingOnly"
                                            checked={provisioningMode === 'existing_only'}
                                            onChange={() => setProvisioningMode('existing_only')}
                                        />
                                        <Label className="form-check-label mb-0" for="existingOnly">
                                            Only let in people already added
                                            <span className="badge badge-light-primary ms-2">Recommended</span>
                                            <span className="f-12 f-light d-block">
                                                Their Microsoft account connects to the user you created. Anyone else is turned away.
                                                </span>
                                        </Label>
                                    </div>

                                    <div className="form-check mb-3">
                                        <Input
                                            type="radio"
                                            name="provisioningMode"
                                            id="autoCreate"
                                            checked={provisioningMode === 'auto_create'}
                                            onChange={() => setProvisioningMode('auto_create')}
                                        />
                                        <Label className="form-check-label mb-0" for="autoCreate">
                                            Create an account for anyone in a linked directory
                                            <span className="f-12 f-light d-block">
                                                New people get in immediately with the starting role below. Every employee in the
                                                directory can sign in.
                                                </span>
                                        </Label>
                                    </div>

                                    <Row>
                                        <Col md="6">
                                            <Label className="form-label">Starting role</Label>
                                            <select
                                                name="startingrole"
                                                className="form-control digits"
                                                value={defaultUserTypeId}
                                                disabled={provisioningMode !== 'auto_create'}
                                                onChange={(e) => setDefaultUserTypeId(e.target.value)}>
                                                <option value="">Select Profile</option>
                                                {userProfiles.map(profile => (
                                                    <option key={profile.user_type_id._id} value={profile.user_type_id._id}>{profile.user_type_id.user_role}</option>
                                                ))}
                                            </select>
                                        </Col>
                                        <Col md="6">
                                            <Label className="form-label">Starting properties</Label>
                                            <div
                                                className="form-control"
                                                style={{
                                                    height: 'auto',
                                                    maxHeight: '150px',
                                                    overflowY: 'auto',
                                                    opacity: provisioningMode === 'auto_create' ? 1 : 0.5,
                                                    pointerEvents: provisioningMode === 'auto_create' ? 'auto' : 'none'
                                                }}>
                                                {properties.length === 0 && (
                                                    <span className="f-12 f-light">No properties found</span>
                                                )}
                                                {properties.map(property => (
                                                    <div key={property._id} className="d-flex align-items-center mb-1">
                                                        <input
                                                            type="checkbox"
                                                            className="me-2"
                                                            checked={selectedProperties.includes(String(property._id))}
                                                            onChange={() => toggleProperty(String(property._id))}
                                                        />
                                                        <span className="f-13">{property.property_name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <span className="f-12 f-light">Leave every box clear to give access to all properties.</span>
                                        </Col>
                                    </Row>
                                </FormGroup>

                            </CardBody>
                            <CardFooter className="text-end">
                                <Btn attrBtn={{ color: 'primary', type: 'button', disabled: saving, onClick: handleSave }}>
                                    {saving ? 'Saving...' : 'Save SSO Configuration'}
                                </Btn>
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default SSOConfigurationAdd;
